
import { createContext, ReactNode, useContext, useState } from 'react'
import { TPatient } from 'src/types/patient'
import ArchiveAccountModal from 'src/components/patient/archive-or-delete/archive-account-modal.component'
import DeleteTestPatientModal from 'src/components/patient/archive-or-delete/delete-test-patient-modal.component'
import MedicalTeamCommentsModal from 'src/components/medical-team-comments/medical-team-comments-modal.component'
import ConsentModal from 'src/components/patient/patient-consent-modal.component'
import GenericFollowupModal from 'src/components/followup/generic-followup-modal.component'
import { useSearchParams } from 'react-router-dom'
import { TPatientFollowup } from 'src/types/followup'
import MedEventModal from 'src/components/med-events/med-event-modal.component'
import { TPatientMedEventForForm } from 'src/components/med-events/med-event-form.component'

type TPatientFollowupModalProps = {
  patientId: number
  patientFollowupIdToEdit: number
}
type TPatientMedEventModalProps = {
  patientId: number
  patientMedEventForForm: TPatientMedEventForForm
}
type TConsentModalProps = {
  patientId: number
}
type TCommentsModalProps = {
  patientId: number
  patientFollowup: Pick<TPatientFollowup, 'id' | 'endReason'>
}
type TArchiveModalProps = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname' | 'isArchived'>
}
type TDeleteModalProps = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
}

type TModalsContext = {
  openFollowupModal: (props: TPatientFollowupModalProps) => void
  openMedEventModal: (props: TPatientMedEventModalProps) => void
  openConsentModal: (props: TConsentModalProps) => void
  openMedicalTeamCommentsModal: (props: TCommentsModalProps) => void
  openArchiveModal: (props: TArchiveModalProps) => void
  openDeleteTestPatientModal: (props: TDeleteModalProps) => void
  closeFollowupModal: () => void
  closeMedEventModal: () => void
  closeConsentModal: () => void
  closeMedicalTeamCommentsModal: () => void
  closeArchiveModal: () => void
  closeDeleteModal: () => void
}

const ModalsContext = createContext<TModalsContext>({} as TModalsContext)
export const useModals = () => useContext(ModalsContext)

export const ModalsProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [followupModal, setFollowupModal] = useState<TPatientFollowupModalProps | null>(null)
  const [medEventModal, setMedEventModal] = useState<TPatientMedEventModalProps | null>(null)
  const [consentModal, setConsentModal] = useState<TConsentModalProps | null>(null)
  const [commentsModal, setCommentsModal] = useState<TCommentsModalProps | null>(null)
  const [archiveModal, setArchiveModal] = useState<TArchiveModalProps | null>(null)
  const [deleteModal, setDeleteModal] = useState<TDeleteModalProps | null>(null)

  const openFollowupModal = (props: TPatientFollowupModalProps) => {
    setFollowupModal(props)
  }
  const openMedEventModal = (props: TPatientMedEventModalProps) => {
    setMedEventModal(props)
  }
  const openConsentModal = (props: TConsentModalProps) => {
    setConsentModal(props)
  }
  const openMedicalTeamCommentsModal = (props: TCommentsModalProps) => {
    setCommentsModal(props)
  }
  const openArchiveModal = (props: TArchiveModalProps) => {
    setArchiveModal(props)
  }
  const openDeleteTestPatientModal = (props: TDeleteModalProps) => {
    setDeleteModal(props)
  }

  const closeFollowupModal = () => {
    setFollowupModal(null)
  }
  const closeMedEventModal = () => {
    setMedEventModal(null)
  }
  const closeConsentModal = () => {
    setConsentModal(null)
  }
  const closeMedicalTeamCommentsModal = () => {
    setCommentsModal(null)
  }
  const closeArchiveModal = () => {
    setArchiveModal(null)
  }
  const closeDeleteModal = () => {
    setDeleteModal(null)
  }

  const contextValue: TModalsContext = {
    openFollowupModal,
    openMedEventModal,
    openConsentModal,
    openMedicalTeamCommentsModal,
    openArchiveModal,
    openDeleteTestPatientModal,
    closeFollowupModal,
    closeMedEventModal,
    closeConsentModal,
    closeMedicalTeamCommentsModal,
    closeArchiveModal,
    closeDeleteModal,
  }

  return (
    <ModalsContext.Provider value={contextValue}>
      {children}

      {followupModal && (
        <GenericFollowupModal
          patientId={followupModal.patientId}
          patientFollowupIdToEdit={followupModal.patientFollowupIdToEdit}
          onToggle={(event?: React.MouseEvent<HTMLElement>) => {
            event?.stopPropagation()
            closeFollowupModal()
          }}
        />
      )}

      {medEventModal && (
        <MedEventModal
          patientId={medEventModal.patientId}
          patientMedEventForForm={medEventModal.patientMedEventForForm}
          onToggle={(event?: React.MouseEvent<HTMLElement>) => {
            event?.stopPropagation()
            closeMedEventModal()
          }}
        />
      )}

      {consentModal && (
        <ConsentModal onClose={() => closeConsentModal()} patientId={consentModal.patientId} />
      )}

      {commentsModal?.patientFollowup && (
        <MedicalTeamCommentsModal
          patientId={commentsModal.patientId}
          patientFollowup={commentsModal.patientFollowup}
          isOpen={true}
          onToggle={(event?: React.MouseEvent<HTMLElement>) => {
            event?.stopPropagation()
            const currentParams = new URLSearchParams(searchParams.toString())
            currentParams.delete('isMedicalTeamCommentOpen')
            setSearchParams(currentParams, { replace: true })
            closeMedicalTeamCommentsModal()
          }}
        />
      )}

      {archiveModal && (
        <ArchiveAccountModal
          patient={archiveModal.patient}
          isModalOpen={true}
          onToggle={(event?: React.MouseEvent<HTMLElement>) => {
            event?.stopPropagation()
            closeArchiveModal()
          }}
        />
      )}

      {deleteModal && (
        <DeleteTestPatientModal
          patient={deleteModal.patient}
          isModalOpen={true}
          onToggle={(event?: React.MouseEvent<HTMLElement>) => {
            event?.stopPropagation()
            closeDeleteModal()
          }}
        />
      )}
    </ModalsContext.Provider>
  )
}
